/**
 * BrandInfo Component
 * Muestra información detallada sobre la marca
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button, Modal, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import Markdown from 'markdown-to-jsx';
import { BrandLogo } from '../../../../../shared/components/BrandLogo';

// Import shared hooks
import { useResponsive } from '../../../../../shared/hooks/useResponsive'; 

import {
  // Estilos base
  brandInfoContainerStyles,
  brandImageStyles,
  brandContentStyles,
  brandDescriptionStyles,
  brandTitleStyles,
  brandBannerContainerStyles,
  brandBannerImageStyles,
  // Estilos para el modal y botón
  moreButtonStyles,
  modalStyles,
  modalHeaderStyles,
  modalImageStyles,
  modalTitleStyles,
  modalDescriptionStyles,
  truncatedDescriptionStyles,
  // Estilos específicos para dispositivos móviles
  mobileBrandInfoContainerStyles,
  mobileBrandImageStyles,
  mobileBrandContentStyles,
  mobileBrandDescriptionStyles,
  mobileModalStyles,
  mobileModalImageStyles,
  mobileModalTitleStyles,
  mobileModalDescriptionStyles,
  mobileContainerStyles,
  // Estilos específicos para tablets
  tabletBrandImageStyles,
  tabletBrandDescriptionStyles,
  tabletModalStyles,
  tabletModalImageStyles,
  tabletModalTitleStyles,
  tabletModalDescriptionStyles,
  tabletBannerContainerStyles,
  tabletBannerImageStyles,
  tabletBrandHeaderBoxStyles,
  // Estilos específicos para escritorio
  desktopBrandInfoContainerStyles,
  desktopBrandImageStyles,
  desktopBrandContentStyles,
  desktopBrandDescriptionStyles,
  desktopModalStyles,
  desktopModalImageStyles,
  desktopModalTitleStyles,
  desktopModalDescriptionStyles,
  desktopBannerContainerStyles,
  desktopBannerImageStyles,
  desktopBrandHeaderBoxStyles,
  // Estilos específicos para banners
  mobileBannerContainerStyles,
  mobileBannerImageStyles,
} from '../styles/index';

import { getTabletSpecificStyles } from '../styles/tablet';

const BrandInfo = ({ brand }) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  // Use the hook
  const { deviceType, tabletType } = useResponsive(); 
  
  // --- Refactored Style Calculation --- 
  const styles = useMemo(() => {
    // Base styles common to all
    const base = {
      container: brandInfoContainerStyles,
      image: { ...brandImageStyles, borderRadius: '4px', objectFit: 'contain' }, // Base overrides
      content: brandContentStyles,
      title: brandTitleStyles,
      description: brandDescriptionStyles,
      bannerContainer: brandBannerContainerStyles,
      bannerImage: brandBannerImageStyles,
      moreButton: moreButtonStyles,
      modal: modalStyles,
      modalHeader: modalHeaderStyles,
      modalImage: modalImageStyles,
      modalTitle: modalTitleStyles,
      modalDescription: modalDescriptionStyles,
      truncatedDescription: truncatedDescriptionStyles,
      brandHeaderBox: {} // Initialize empty, specific below
    };

    // Device-specific overrides
    let specific = {};
    if (deviceType === 'mobile') {
      specific = {
        container: mobileContainerStyles,
        image: { ...mobileBrandImageStyles, width: '60px', height: '60px' },
        content: mobileBrandContentStyles,
        description: mobileBrandDescriptionStyles,
        bannerContainer: mobileBannerContainerStyles,
        bannerImage: mobileBannerImageStyles,
        modal: mobileModalStyles,
        modalImage: mobileModalImageStyles,
        modalTitle: mobileModalTitleStyles,
        modalDescription: mobileModalDescriptionStyles,
        brandHeaderBox: { marginRight: 0, width: '100%', marginBottom: 2 }
      };
    } else if (deviceType === 'tablet') {
      const tabletSpecific = getTabletSpecificStyles(tabletType);
      specific = {
        image: { 
          ...tabletBrandImageStyles, 
          ...tabletSpecific.brandImageStyles, // Tablet model specific
          width: '80px', 
          height: '80px' 
        },
        title: { fontSize: 20 }, // Override base
        description: tabletBrandDescriptionStyles,
        bannerContainer: tabletBannerContainerStyles,
        bannerImage: tabletBannerImageStyles,
        modal: tabletModalStyles,
        modalImage: tabletModalImageStyles,
        modalTitle: tabletModalTitleStyles,
        modalDescription: tabletModalDescriptionStyles,
        brandHeaderBox: { ...tabletBrandHeaderBoxStyles, marginRight: 3, width: 'auto', marginBottom: 0 } // Merge from tablet styles
      };
    } else { // Desktop
      specific = {
        container: desktopBrandInfoContainerStyles,
        image: { ...desktopBrandImageStyles, width: '90px', height: '90px' },
        content: desktopBrandContentStyles,
        title: { fontSize: 22 }, // Override base
        description: desktopBrandDescriptionStyles,
        bannerContainer: desktopBannerContainerStyles,
        bannerImage: desktopBannerImageStyles,
        modal: desktopModalStyles,
        modalImage: desktopModalImageStyles,
        modalTitle: desktopModalTitleStyles,
        modalDescription: desktopModalDescriptionStyles,
        brandHeaderBox: { ...desktopBrandHeaderBoxStyles, marginRight: 3, width: 'auto', marginBottom: 0 } // Merge from desktop styles
      };
    }

    // Deep merge base and specific styles (simple merge for demo)
    // A proper deep merge utility might be needed for complex cases
    const merged = {};
    Object.keys(base).forEach(key => {
      merged[key] = { ...base[key], ...(specific[key] || {}) };
    });

    return merged;

  }, [deviceType, tabletType]); // Recalculate only when deviceType/tabletType changes
  // --- End Refactored Style Calculation ---
  
  // Removed original getResponsiveStyles function and the call to it
  
  // Simplified brandHeaderBoxStyles definition using merged styles
  const brandHeaderBoxStyles = styles.brandHeaderBox;
  
  const brandMetaBoxStyles = {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 2,
    justifyContent: 'center'
  };
  
  const newContainerStyles = {
    ...styles.container,
    flexDirection: deviceType === 'mobile' ? 'column' : 'row',
    alignItems: deviceType === 'mobile' ? 'flex-start' : 'center',
  };
  
  const descriptionBoxStyles = {
    flexGrow: 1,
    maxWidth: deviceType === 'mobile' ? '100%' : '60%',
    marginLeft: deviceType === 'mobile' ? 0 : 4, // Aumentando el margen izquierdo en tablet y desktop
    position: 'relative', // Para posicionar el botón "More"
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <>
      {/* Banner Image */}
      {brand.bannerImage && (
        <Box sx={styles.bannerContainer}>
          <Box
            component="img"
            src={brand.bannerImage}
            alt={`${brand.name} banner`}
            sx={styles.bannerImage}
          />
        </Box>
      )}

      <Box sx={deviceType === 'mobile' ? mobileBrandInfoContainerStyles : newContainerStyles}>
        {/* Bloque izquierdo: imagen y título */}
        <Box sx={brandHeaderBoxStyles}>
          {brand.logo && (
            <BrandLogo 
              src={brand.logo}
              alt={`${brand.name} logo`}
              sx={styles.image}
            />
          )}
          
          <Box sx={brandMetaBoxStyles}>
            <Typography sx={styles.title}>
              {brand.name}
            </Typography>
          </Box>
        </Box>
        
        {/* Bloque derecho: descripción */}
        {brand.description && (
          <Box sx={descriptionBoxStyles}>
            <Typography variant="body1" sx={{...styles.description, ...styles.truncatedDescription}}>
              {brand.description}
            </Typography>
            <Button 
              variant="text" 
              size="small" 
              sx={styles.moreButton}
              onClick={handleOpenModal}
            >
              {t('brands.brandInfo.seeMore')}
            </Button>
          </Box>
        )}

        {/* Modal con descripción completa */}
        <Modal
          open={modalOpen}
          onClose={handleCloseModal}
          aria-labelledby="brand-details-title"
          aria-describedby="brand-details-description"
        >
          <Box sx={styles.modal}>
            <Box sx={styles.modalHeader}>
              <Typography id="brand-details-title" variant="h6" component="h2" sx={styles.modalTitle}>
                {brand.name}
              </Typography>
              <IconButton onClick={handleCloseModal}>
                <CloseIcon />
              </IconButton>
            </Box>
            <BrandLogo 
              src={brand.logo} 
              alt={`${brand.name} logo`} 
              sx={styles.modalImage}
            />
            <Box id="brand-details-description" sx={styles.modalDescription}>
              <Markdown>{brand.description || ''}</Markdown>
            </Box>
          </Box>
        </Modal>
      </Box>
    </>
  );
};

BrandInfo.propTypes = {
  brand: PropTypes.shape({
    name: PropTypes.string.isRequired,
    logo: PropTypes.string,
    bannerImage: PropTypes.string,
    description: PropTypes.string,
    verified: PropTypes.bool,
    additionalInfo: PropTypes.shape({
      warranty: PropTypes.string,
      rewardsProgram: PropTypes.string,
      website: PropTypes.string
    })
  }).isRequired,
};

export default BrandInfo; 